// frontend/packages/business-console/src/services/cloudinaryDirectUpload.js
//
// Signed upload of ONE file straight to Cloudinary's upload API, without the
// Upload Widget. The library uses this for multi-file uploads: the widget
// takes a single public_id per open, so it could only ever do one file at a
// time (useCloudinaryUploadWidget forces multiple=false for that reason).
//
// Per file:
//   1. upload-intent: the backend builds the tenant-scoped public_id
//      (upryzze/<businessId>/prod/YYYY/MM/<slug>-<rand>) from the real
//      filename, plus the business tag and upload preset.
//   2. signature: the backend signs exactly the params we send. It refuses a
//      public_id outside upryzze/<businessId>/, so tenant scoping is enforced
//      server-side as before.
//   3. POST the file to Cloudinary with XHR, for upload progress.
//
// The resolved value is Cloudinary's upload response, the same shape as the
// widget's result.info, so it feeds createMediaAssetFromCloudinary as-is.

import {
  createCloudinaryUploadIntent,
  getCloudinaryUploadSignature,
} from './mediaService';

export function resourceTypeForFile(file) {
  return String(file?.type || '').startsWith('video/') ? 'video' : 'image';
}

function postToCloudinary({ url, form, onProgress, signal }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };

    xhr.onload = () => {
      let body = null;
      try {
        body = JSON.parse(xhr.responseText || 'null');
      } catch {
        // fall through to the status check
      }
      if (xhr.status >= 200 && xhr.status < 300 && body?.public_id) {
        resolve(body);
        return;
      }
      reject(
        new Error(
          body?.error?.message || `Upload failed (HTTP ${xhr.status || 0}).`
        )
      );
    };

    xhr.onerror = () => reject(new Error('Network error during upload.'));
    xhr.onabort = () => reject(new Error('Upload cancelled.'));

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.send(form);
  });
}

export async function uploadFileToCloudinary(file, { onProgress, signal } = {}) {
  if (!file) throw new Error('File is required.');

  const resourceType = resourceTypeForFile(file);

  const intent = await createCloudinaryUploadIntent({
    resourceType,
    filename: file.name || 'upload',
    bytes: file.size,
    folder: '',
    tags: [],
  });

  const cloudName = String(intent?.cloudName || '').trim();
  const apiKey = String(intent?.apiKey || '').trim();
  const publicId = String(intent?.params?.public_id || '').trim();
  const uploadPreset = String(intent?.params?.upload_preset || '').trim();
  const tags = String(intent?.params?.tags || '').trim();

  if (!cloudName || !apiKey || !publicId) {
    throw new Error(
      'Invalid upload intent (missing cloudName, apiKey, or public_id).'
    );
  }
  if (!uploadPreset) {
    throw new Error(
      'Cloudinary upload preset is not configured. Set CLOUDINARY_UPLOAD_PRESET on the backend and redeploy.'
    );
  }

  // Exactly what gets POSTed, minus file/api_key (Cloudinary excludes those
  // from the signature). resource_type is in the URL path, not a param.
  const params = {
    public_id: publicId,
    timestamp: Math.floor(Date.now() / 1000),
    upload_preset: uploadPreset,
  };
  if (tags) params.tags = tags;

  const signed = await getCloudinaryUploadSignature(params);
  const signature = String(signed?.signature || '').trim();
  if (!signature) throw new Error('Signature endpoint returned no signature.');

  const form = new FormData();
  form.append('file', file);
  form.append('api_key', apiKey);
  form.append('signature', signature);
  Object.entries(params).forEach(([k, v]) => form.append(k, String(v)));

  return postToCloudinary({
    url: `https://api.cloudinary.com/v1_1/${encodeURIComponent(cloudName)}/${resourceType}/upload`,
    form,
    onProgress,
    signal,
  });
}
